import React, { useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Upload, FileText, Trash2, Loader2, Download, ChevronDown, X, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPES = [
  { value: "general", label: "General" },
  { value: "tax_form", label: "Tax Form" },
  { value: "id_verification", label: "ID Verification" },
  { value: "accreditation", label: "Accreditation" },
  { value: "agreement", label: "Agreement" },
  { value: "correspondence", label: "Correspondence" },
  { value: "compliance", label: "Compliance" },
];

const SIZE_FMT = (bytes) => bytes > 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;

export default function ProfileDocuments({ userId, documents = [], isAdmin = false, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [docType, setDocType] = useState("general");
  const [notes, setNotes] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const fileRef = useRef(null);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const profileDocs = documents.filter(d => d.is_profile_doc);
  const dealDocs = documents.filter(d => !d.is_profile_doc);

  const handleUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API}/documents/profile/upload?target_user_id=${userId}&doc_type=${docType}&notes=${encodeURIComponent(notes)}`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setSuccessMsg(`"${file.name}" uploaded successfully`);
      setTimeout(() => setSuccessMsg(""), 3000);
      setShowUpload(false);
      setNotes("");
      setDocType("general");
      if (onRefresh) onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }, [userId, docType, notes, onRefresh]);

  const handleDelete = useCallback(async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    setDeleting(docId);
    try {
      await axios.delete(`${API}/documents/profile/${docId}`, { headers });
      if (onRefresh) onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(null);
    }
  }, [onRefresh]);

  const handleDownload = useCallback(async (docId, filename) => {
    try {
      const res = await axios.get(`${API}/documents/download/${docId}`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert("Download failed");
    }
  }, []);

  return (
    <div className="space-y-4" data-testid="profile-documents">
      {/* Success Message */}
      <AnimatePresence>
        {successMsg && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-50 border border-emerald-200 text-xs font-medium text-emerald-700" data-testid="upload-success">
            <CheckCircle2 className="h-4 w-4" /> {successMsg}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload Section (Admin only) */}
      {isAdmin && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
          <div className="flex items-center justify-between">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#999]">Upload to Profile</h4>
            <button onClick={() => setShowUpload(!showUpload)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#022c22] text-[#e5c98b] text-[10px] font-medium hover:bg-[#064e3b] transition-colors"
              data-testid="toggle-upload-form">
              <Upload className="h-3 w-3" /> {showUpload ? "Cancel" : "Upload Document"}
            </button>
          </div>
          <AnimatePresence>
            {showUpload && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden">
                <div className="mt-4 space-y-3">
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="text-[10px] text-[#888] block mb-1">Document Type</label>
                      <div className="relative">
                        <select value={docType} onChange={e => setDocType(e.target.value)}
                          className="w-full appearance-none px-3 py-2 pr-8 rounded-lg border border-[#e8e0d0] text-xs bg-white outline-none"
                          data-testid="doc-type-select">
                          {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-[#999] pointer-events-none" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <label className="text-[10px] text-[#888] block mb-1">Notes (optional)</label>
                      <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. 2025 K-1 form"
                        className="w-full px-3 py-2 rounded-lg border border-[#e8e0d0] text-xs outline-none" data-testid="doc-notes-input" />
                    </div>
                  </div>
                  <label className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-[#e8e0d0] bg-[#faf7f2] cursor-pointer hover:border-emerald-400 transition-colors"
                    data-testid="file-upload-area">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin text-emerald-600" /> : <Upload className="h-4 w-4 text-[#999]" />}
                    <span className="text-xs text-[#666]">{uploading ? "Uploading..." : "Click to select file (max 20MB)"}</span>
                    <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="file-input" />
                  </label>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Profile Documents */}
      {profileDocs.length > 0 && (
        <div>
          <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-2">Profile Documents</h4>
          <div className="space-y-2">
            {profileDocs.map(d => (
              <div key={d.doc_id} className="rounded-xl border border-[#e8e0d0] bg-white p-3 flex items-center justify-between group" data-testid={`profile-doc-${d.doc_id}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="p-2 rounded-lg bg-blue-50 border border-blue-100">
                    <FileText className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[#333] truncate">{d.original_filename}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-[9px] font-medium">{d.doc_type}</span>
                      <span className="text-[9px] text-[#bbb]">{SIZE_FMT(d.size || 0)}</span>
                      {d.uploaded_by && <span className="text-[9px] text-[#bbb]">by {d.uploaded_by}</span>}
                      {d.notes && <span className="text-[9px] text-[#999] italic">{d.notes}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => handleDownload(d.doc_id, d.original_filename)}
                    className="p-1.5 rounded-md hover:bg-emerald-50 text-[#888] hover:text-emerald-600 transition-colors" data-testid={`download-doc-${d.doc_id}`}>
                    <Download className="h-3.5 w-3.5" />
                  </button>
                  {isAdmin && (
                    <button onClick={() => handleDelete(d.doc_id)} disabled={deleting === d.doc_id}
                      className="p-1.5 rounded-md hover:bg-red-50 text-[#888] hover:text-red-500 transition-colors" data-testid={`delete-doc-${d.doc_id}`}>
                      {deleting === d.doc_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Deal Documents */}
      {dealDocs.length > 0 && (
        <div>
          <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-2">Deal Documents</h4>
          <div className="space-y-2">
            {dealDocs.map(d => (
              <div key={d.doc_id} className="rounded-xl border border-[#e8e0d0] bg-white p-3 flex items-center gap-3" data-testid={`deal-doc-${d.doc_id}`}>
                <FileText className="h-4 w-4 text-emerald-600 shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-[#333] truncate">{d.original_filename}</p>
                  <span className="text-[10px] text-[#999]">{d.doc_type}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {profileDocs.length === 0 && dealDocs.length === 0 && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-8 text-center text-sm text-[#888]" data-testid="no-documents">
          No documents.{isAdmin && " Use the upload button above to add documents to this profile."}
        </div>
      )}
    </div>
  );
}
